import "./DeliveryAddressSelection.scss";

import PropTypes from "prop-types";

import * as React from "react";
import Slider from "react-slick";
import { FormattedMessage } from "react-intl";
import { InputGroup, FormControl, Button, Col, Row, Panel } from "react-bootstrap";
import CustomerAddressBadge from "./CustomerAddressBadge";
import { FsdAddressType } from "shared/src/types/customer/FsdAddress";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import ClearFixUtil from "../../../../utils/ClearFixUtil";
import FilterStoreUtil from "../../../general/globalheader/store/FilterStoreUtil";
import Customer from "shared/src/types/customer/Customer";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { connect } from "shared/src/stores/storeConnect";
import { isEmpty } from "shared/src/utils/util";

const SHOW_SEARCH_BAR_NUMBER = 6;
const LAYOUT_CONFIG = { xs: 1, sm: 1, md: 3, lg: 4, xl: 4 };

import { StoreType } from "../../../../datatypes/Store";
import { notify } from "shared/src/utils/Notifications";
import CustomerStore from "../../../../stores/CustomerStore";
import MetroStoresStore from "../../../../stores/MetroStoresStore";

/**
 * Decides what to display: either both: the delivery address selection AND the mario store selection
 * or just the delivery address selection.
 */
type Props = {
    onAddressSelect: () => void;
    customer: Customer | null | undefined;
    hideTitle: boolean;
    stores: StoreType[]; // information about all available metro stores,
    renderInPopover: boolean;
};

type State = {
    allAddresses: FsdAddressType[];
    filteredAddresses: FsdAddressType[] | undefined | null;
    searchInput: string;
    selectedAddressHash: string | undefined | null;
};

class DeliveryAddressSelection extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = this.getStateFromStore(props);
    }

    componentWillReceiveProps(newProps: Props) {
        this.setState(this.getStateFromStore(newProps));
    }

    getStateFromStore(props: Props): State {
        const selectedAddressHash: string | undefined | null = SessionStore.getSelectedDeliveryAddressHash();
        const sortedAddresses = this.getAddresses(selectedAddressHash, props);

        return {
            allAddresses: sortedAddresses || [],
            filteredAddresses: sortedAddresses,
            searchInput: "",
            selectedAddressHash: selectedAddressHash
        };
    }

    displaySlider(): boolean {
        return this.props.renderInPopover && this.state.allAddresses.length > 3;
    }

    getStoreName(storeNumber: string | undefined | null, props: Props): string | undefined | null {
        const store = props.stores?.find(
            (store) => store && store.storeId && store.storeId.locationNumber === storeNumber
        );
        if (store) {
            return store.storeDisplayName;
        }
        return null;
    }

    fieldContains(field: string | undefined | null, searchInput: string | undefined): boolean {
        if (!field || !searchInput) {
            return false;
        }
        return field.toLowerCase().includes(searchInput.toLowerCase());
    }

    filterAddresses(searchInput: string | undefined) {
        const allAddresses = this.state.allAddresses;
        if (isEmpty(searchInput)) {
            return allAddresses;
        }
        return (allAddresses ?? []).filter((address) => {
            return (
                (address && this.fieldContains(address.zipcode, searchInput)) ||
                this.fieldContains(address.city, searchInput) ||
                this.fieldContains(address.street, searchInput) ||
                this.fieldContains(address.storeName, searchInput)
            );
        });
    }

    storeIsSelectable(address: FsdAddressType) {
        if (!address || !address.accessibleByCurrentUser) {
            // note that this can only happen, if the user hacks the html, because we make these inaccessible in the css/html
            const message = "You are not allowed to select this address.";
            TrackingUtil.trackUiErrorSd("AddressSelectionPage", message);
            return false;
        }
        const externalRedirectUrl = address.nonProductionRedirectUrl;
        if (externalRedirectUrl) {
            const notification = {
                type: "warning" as const,
                key: "EX_SELECT_NON_PRODUCTION_ADDRESS",
                values: [],
                id: "EX_SELECT_NON_PRODUCTION_ADDRESS"
            };
            notify(notification);
            return false;
        }
        return true;
    }

    onSelectAddress(address: FsdAddressType, event: React.SyntheticEvent) {
        event.preventDefault();
        if (this.storeIsSelectable(address)) {
            SessionStore.setDeliveryAddress(address);
            this.setState({ selectedAddressHash: address.hash });
            this.props.onAddressSelect();
        }
    }

    onChangeSearch(source: React.SyntheticEvent) {
        this.setState({
            searchInput: (source.target as any).value,
            filteredAddresses: this.filterAddresses((source.target as any).value)
        });
    }

    getSliderSettings() {
        return {
            dots: true,
            infinite: false,
            speed: 500,
            slidesToShow: 3,
            slidesToScroll: 3,
            responsive: [
                { breakpoint: 321, settings: { slidesToShow: 1, slidesToScroll: 1 } },
                { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 2 } },
                { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 3 } }
            ]
        };
    }

    getAddresses(selectedAddressHash: string | undefined | null, props: Props) {
        const customer = props.customer;
        const customerAddresses = FilterStoreUtil.getCustomerDeliveryAddresses(customer);

        const selectedAddress = (customerAddresses ?? []).filter((a) => a.hash === selectedAddressHash);

        const sortedWithoutSelected = [...customerAddresses]
            .sort((a, b) => a.zipcode.localeCompare(b.zipcode))
            .filter((a) => a.hash !== selectedAddressHash);

        const merged = [...new Set([...selectedAddress, ...sortedWithoutSelected])];
        return merged.map((address) => {
            address.storeName = this.getStoreName(address.deliveryStore, props);
            return address;
        });
    }

    getAddressBadges(): Array<React.ReactNode> {
        return (this.state.filteredAddresses ?? []).map((address) => (
            <CustomerAddressBadge
                key={address.hash}
                address={address}
                selected={address.hash === this.state.selectedAddressHash}
                clickHandler={this.onSelectAddress.bind(this)}
                renderInPopover={this.props.renderInPopover}
            />
        ));
    }

    getSliderAddresses(): Array<React.ReactNode> {
        return (this.getAddressBadges() ?? []).map((address, rank: number) => (
            <div key={"slider-" + rank}>{address}</div>
        ));
    }

    getResponsiveAddresses() {
        const largeScreenSize = this.props.renderInPopover ? 4 : 3;
        const smallScreenSize = this.props.renderInPopover ? 4 : 6;
        const addressTiles = (this.getAddressBadges() ?? []).map((address, rank: number) => (
            <Col xs={12} sm={smallScreenSize} md={4} lg={largeScreenSize} key={"column-" + rank}>
                {address}
            </Col>
        ));
        return ClearFixUtil.addClearfix(addressTiles, LAYOUT_CONFIG);
    }

    renderAddressesInSlider() {
        const addresses = this.getSliderAddresses();
        return (
            <div className="mfcss_address-slider clearfix">
                {addresses.length > 0 ? <Slider {...this.getSliderSettings()}>{addresses}</Slider> : null}
            </div>
        );
    }

    renderAddresses() {
        return (
            <Panel className="panel-sm no-border no-margin panel panel-default mfcss_address-selector">
                <Row>{this.getResponsiveAddresses()}</Row>
            </Panel>
        );
    }

    renderSearchbar() {
        const searchPlaceholder = this.context.intl.formatMessage({
            id: "EX_MOBILE_FILTER_STORES_PLACEHOLDER"
        });
        return (
            <Row>
                <Col xs={12} sm={6} md={8} lg={8}>
                    <h3 className="ex-address-search-header">
                        <FormattedMessage
                            id={this.props.renderInPopover ? "EX_FILTER_STORES" : "EX_MOBILE_FILTER_STORES"}
                        />
                    </h3>
                </Col>
                <Col xs={12} sm={6} md={4} lg={4}>
                    <InputGroup className="mfcss_input-group-inset input-group-block">
                        <FormControl
                            type="text"
                            placeholder={searchPlaceholder}
                            value={this.state.searchInput}
                            onChange={this.onChangeSearch.bind(this) as any}
                        />
                        <InputGroup.Button>
                            <Button bsStyle="link" className="btn-square">
                                <i className="icon-magnify icon-lg text-primary" />
                            </Button>
                        </InputGroup.Button>
                    </InputGroup>
                </Col>
            </Row>
        );
    }

    renderAddressSelection() {
        if (this.props.renderInPopover) {
            if (this.displaySlider()) {
                return (
                    <div>
                        {this.renderAddressesInSlider()}
                        <div className="popover-well">{this.renderSearchbar()}</div>
                    </div>
                );
            }
            return this.renderAddresses();
        }
        return (
            <div>
                {this.state.allAddresses.length > SHOW_SEARCH_BAR_NUMBER ? (
                    <Panel
                        className={"panel-sm no-border no-margin background-primary-reduced-more panel panel-default"}>
                        {this.renderSearchbar()}
                    </Panel>
                ) : null}
                {this.renderAddresses()}
            </div>
        );
    }

    formatTitle(message: string) {
        return this.props.renderInPopover ? (
            <h3 className="margin-grid-top">
                <FormattedMessage id={message} />
            </h3>
        ) : (
            <h2 className="margin-grid-top-2x margin-grid-left-2x">
                <FormattedMessage id={message} />
            </h2>
        );
    }

    render() {
        if (this.state.allAddresses.length === 0) {
            return this.formatTitle("EX_DELIVERY_ADDRESS_SELECT_NO_ADDRESSES");
        }
        const title = this.formatTitle("EX_DELIVERY_ADDRESS_SELECT");
        return (
            <div>
                {!this.props.hideTitle && title}
                {this.renderAddressSelection()}
            </div>
        );
    }

    static contextTypes = { intl: PropTypes.object };
}

export default connect(
    {
        stores: [
            [
                MetroStoresStore,
                (metroStores: typeof MetroStoresStore) => ({
                    stores: metroStores.getStores()
                })
            ],
            [
                CustomerStore,
                (s: typeof CustomerStore) => ({
                    customer: s.getCustomer()
                })
            ]
        ]
    },
    DeliveryAddressSelection
);
